import React, { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/browser";
import { fmtCcy, fmtUnits, formatFriendly } from "../helpers";
import {
  ApiMissedPayment,
  ReconRow,
  ApiCompanyReconciliationSummary,
  RowExplanation,
} from "../types";
import { request } from "../../../helpers/utilities";
import {
  GET_RECONCILIATION_MISSED_PAYMENTS,
  GET_RECONCILIATION_TRANSACTIONS,
  GET_RECONCILIATION_ENTRY_COMPANIES,
  GET_RECONCILIATION_ROW_EXPLANATIONS,
} from "../../../gql/productStatements";
import Icon from "../Icon";
import RowProblemCard from "./RowProblemCard";

interface ApiTx {
  id: string;
  statementUploadSheetId: string;
  rowNumber: number;
  valueDate: string;
  transactionDate: string | null;
  transactionAmount: number | null;
  units: number | null;
  drcr: "C" | "D";
  transactionReference: string;
  transactionDetails: string | null;
  deviated: boolean;
  pending: boolean;
  parseError: string | null;
}

const FALLBACK_LIMIT = 500;

const transactionsCache: Record<string, ApiTx[]> = {};
const companiesCache: Record<string, ApiCompanyReconciliationSummary[]> = {};
const missedPaymentsCache: Record<string, ApiMissedPayment[]> = {};
const explanationsCache: Record<string, RowExplanation[]> = {};

const BANNER_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-2)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "8px 12px",
  margin: "8px 0 12px",
};

const NarrationSection: React.FC<{
  row: ReconRow;
  activeTab: "summary" | "companies" | "transactions";
}> = ({ row, activeTab }) => {
  const [expanded, setExpanded] = useState(false);
  const [txs, setTxs] = useState<ApiTx[] | null>(null);
  const [missedPayments, setMissedPayments] = useState<
    ApiMissedPayment[] | null
  >(null);
  const [companies, setCompanies] = useState<
    ApiCompanyReconciliationSummary[] | null
  >(null);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [explanations, setExplanations] = useState<RowExplanation[]>([]);

  useEffect(() => {
    let current = true;
    setExplanations([]);
    if (activeTab !== "transactions" || !row.id) return undefined;

    const entryId = row.id;
    if (explanationsCache[entryId]) {
      setExplanations(explanationsCache[entryId]);
      return undefined;
    }

    request({
      query: GET_RECONCILIATION_ROW_EXPLANATIONS,
      variables: { id: entryId },
    })
      .then(
        (response: {
          data: {
            data: {
              reconciliationEntry: { rowExplanations: RowExplanation[] } | null;
            };
          };
        }) => {
          const data =
            response?.data?.data?.reconciliationEntry?.rowExplanations ?? [];
          explanationsCache[entryId] = data;
          if (current) setExplanations(data);
        },
      )
      .catch((error: unknown) => Sentry.captureException(error));

    return () => {
      current = false;
    };
  }, [activeTab, row.id]);

  const explanationsByRow = useMemo(
    () => new Map(explanations.map((e) => [e.statementUploadRowId, e])),
    [explanations],
  );
  const [missedPaymentsError, setMissedPaymentsError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!row.productId || !row.priceDate) {
      setTxs([]);
      return;
    }

    const txKey = `${row.productId}_${row.priceDate}_${row.statementUploadId}_${
      row.reversedAt || "active"
    }`;
    if (transactionsCache[txKey]) {
      setTxs(transactionsCache[txKey]);
      setLoadError(null);
      return;
    }

    setTxs(null);
    setLoadError(null);

    request({
      query: GET_RECONCILIATION_TRANSACTIONS,
      variables: {
        productId: row.productId,
        priceDate: row.priceDate,
        statementUploadId: row.statementUploadId,
        limit: Math.max(row.txCount, FALLBACK_LIMIT),
      },
    })
      .then(
        (response: {
          data: { data: { reconciliationTransactions: ApiTx[] } };
        }) => {
          const data = response?.data?.data?.reconciliationTransactions ?? [];
          transactionsCache[txKey] = data;
          setTxs(data);
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setTxs([]);
        setLoadError("We couldn’t load the underlying transactions.");
      });
  }, [
    row.productId,
    row.priceDate,
    row.statementUploadId,
    row.txCount,
    row.reversedAt,
  ]);

  useEffect(() => {
    if (!row.id) {
      setCompanies([]);
      return;
    }

    const compKey = `${row.id}_${row.reversedAt || "active"}`;
    if (companiesCache[compKey]) {
      setCompanies(companiesCache[compKey]);
      setCompaniesLoading(false);
      return;
    }

    setCompanies(null);
    setCompaniesLoading(true);
    request({
      query: GET_RECONCILIATION_ENTRY_COMPANIES,
      variables: { id: row.id },
    })
      .then(
        (response: {
          data: {
            data: {
              reconciliationEntry: {
                companies: ApiCompanyReconciliationSummary[];
              };
            };
          };
        }) => {
          const data =
            response?.data?.data?.reconciliationEntry?.companies ?? [];
          companiesCache[compKey] = data;
          setCompanies(data);
          setCompaniesLoading(false);
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setCompanies([]);
        setCompaniesLoading(false);
      });
  }, [row.id, row.reversedAt]);

  // Separate effect so a txCount refresh doesn't refetch missed payments (and
  // vice versa) — each fetch reruns only when its own inputs change.
  useEffect(() => {
    setMissedPaymentsError(null);
    if (!row.productId || !row.priceDate || row.missedPaymentCount <= 0) {
      setMissedPayments([]);
      return;
    }

    const missedKey = `${row.productId}_${row.priceDate}_${
      row.statementUploadId
    }_${row.reversedAt || "active"}`;
    if (missedPaymentsCache[missedKey]) {
      setMissedPayments(missedPaymentsCache[missedKey]);
      return;
    }

    setMissedPayments(null);

    request({
      query: GET_RECONCILIATION_MISSED_PAYMENTS,
      variables: {
        productId: row.productId,
        priceDate: row.priceDate,
        statementUploadId: row.statementUploadId,
        limit: Math.max(row.missedPaymentCount, FALLBACK_LIMIT),
      },
    })
      .then(
        (response: {
          data: {
            data: { reconciliationMissedPayments: ApiMissedPayment[] };
          };
        }) => {
          const data = response?.data?.data?.reconciliationMissedPayments ?? [];
          missedPaymentsCache[missedKey] = data;
          setMissedPayments(data);
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setMissedPayments([]);
        setMissedPaymentsError("We couldn’t load the missed payments.");
      });
  }, [
    row.productId,
    row.priceDate,
    row.statementUploadId,
    row.missedPaymentCount,
    row.reversedAt,
  ]);

  // Deviated rows are the actionable ones (they don't have a matching Phoenix
  // payment) — surface them at the top, then pending, then matched, so the
  // first slice the user sees is the work, not the noise.
  const sortedTxs = useMemo(() => {
    if (!txs) return null;
    const rank = (tx: ApiTx): number => (tx.deviated ? 0 : tx.pending ? 1 : 2);
    return [...txs].sort((a, b) => rank(a) - rank(b));
  }, [txs]);
  const shown = sortedTxs ? (expanded ? sortedTxs : sortedTxs.slice(0, 5)) : [];

  const sortedCompanies = useMemo(() => {
    if (!companies) return null;
    return [...companies].sort((a, b) => {
      const aMiss = a.matchStatus === "mismatched" ? 0 : 1;
      const bMiss = b.matchStatus === "mismatched" ? 0 : 1;
      return aMiss - bMiss;
    });
  }, [companies]);

  const deviatedCount = (txs ?? []).filter((tx) => tx.deviated).length;
  // Use the stored count, not the fetched array length: a failed missed-payments
  // fetch sets [] and would otherwise make the banner falsely assert "nothing missing"
  // while the error notice below says the fetch failed.
  const missedCount = row.missedPaymentCount;

  const banner = useMemo(() => {
    if (row.match !== "mismatched") return null;
    if (deviatedCount === 0 && missedCount === 0) {
      return (
        <div style={BANNER_STYLE}>
          All {txs?.length ?? row.txCount} upload rows match Phoenix by amount,
          and no Phoenix payments are missing from this upload — the mismatch is
          in the aggregate totals (price or units).
        </div>
      );
    }
    if (deviatedCount > 0 && missedCount === 0) {
      return (
        <div style={BANNER_STYLE}>
          <strong style={{ color: "var(--danger)" }}>
            {deviatedCount} of {txs?.length ?? row.txCount} rows
          </strong>{" "}
          don&rsquo;t have a matching Phoenix payment and are shown first below.
        </div>
      );
    }
    if (deviatedCount === 0 && missedCount > 0) {
      return (
        <div style={BANNER_STYLE}>
          <strong style={{ color: "var(--danger)" }}>
            {missedCount} Phoenix payment{missedCount > 1 ? "s" : ""}
          </strong>{" "}
          {missedCount > 1 ? "are" : "is"} not included in this upload.
        </div>
      );
    }
    return (
      <div style={BANNER_STYLE}>
        <strong style={{ color: "var(--danger)" }}>
          {deviatedCount} of {txs?.length ?? row.txCount} rows
        </strong>{" "}
        don&rsquo;t match Phoenix, and{" "}
        <strong style={{ color: "var(--danger)" }}>
          {missedCount} Phoenix payment{missedCount > 1 ? "s" : ""}
        </strong>{" "}
        {missedCount > 1 ? "are" : "is"} not in the upload.
      </div>
    );
  }, [row.match, row.txCount, deviatedCount, missedCount, txs]);

  const toggleCompany = (companyId: string) => {
    setActiveCompanyId((prev) => (prev === companyId ? null : companyId));
  };

  return (
    <>
      {activeTab === "summary" && (
        <>
          {row.match === "mismatched" && banner}

          {/* Missed payments */}
          {missedPaymentsError && (
            <div
              style={{
                color: "var(--danger)",
                fontSize: 13,
                padding: "10px 0",
              }}
            >
              {missedPaymentsError}
            </div>
          )}
          {missedPayments && missedPayments.length > 0 ? (
            <>
              <div className="section-label" style={{ marginTop: 22 }}>
                Payments recorded in Phoenix but missing from this upload
              </div>
              <div className="narr-tbl">
                <div
                  className="narr-head"
                  style={{
                    display: "inline-grid",
                    gridTemplateColumns: "130px 140px 120px 130px 80px 350px",
                    width: "max-content",
                    minWidth: "100%",
                  }}
                >
                  <div>Value date</div>
                  <div>Transaction date</div>
                  <div>Reference</div>
                  <div style={{ textAlign: "right" }}>Amount</div>
                  <div>Dr/Cr</div>
                  <div>Transaction details</div>
                </div>
                {missedPayments.map((tx) => (
                  <div
                    className="narr-row is-missed"
                    key={tx.id}
                    style={{
                      display: "inline-grid",
                      gridTemplateColumns: "130px 140px 120px 130px 80px 350px",
                      width: "max-content",
                      minWidth: "100%",
                    }}
                  >
                    <div>{formatFriendly(tx.valueDate)}</div>
                    <div>
                      {tx.transactionDate
                        ? formatFriendly(tx.transactionDate)
                        : "—"}
                    </div>
                    <div className="mono">{tx.taId || "—"}</div>
                    <div className="mono" style={{ textAlign: "right" }}>
                      {tx.transactionAmount == null
                        ? "—"
                        : fmtCcy(Math.abs(tx.transactionAmount))}
                    </div>
                    <div>{tx.drcr === "debit" ? "Debit" : "Credit"}</div>
                    <div
                      className="narr-text"
                      title={tx.transactionDetails || ""}
                    >
                      {tx.transactionDetails || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            missedPayments &&
            missedPayments.length === 0 && (
              <div
                style={{
                  color: "var(--muted)",
                  fontSize: 13,
                  padding: "15px 0",
                  textAlign: "center",
                  background: "var(--surface-2)",
                  borderRadius: 8,
                  marginTop: 10,
                }}
              >
                No missing payments detected for this entry.
              </div>
            )
          )}
        </>
      )}

      {activeTab === "companies" && (
        <>
          <div
            className="section-label"
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Companies breakdown</span>
            {companies && (
              <span
                style={{ color: "var(--muted)", fontSize: 12, fontWeight: 500 }}
              >
                {companies.length}{" "}
                {companies.length === 1 ? "company" : "companies"}
              </span>
            )}
          </div>

          {companies === null && (
            <div
              style={{ color: "var(--muted)", fontSize: 13, padding: "10px 0" }}
            >
              Loading companies…
            </div>
          )}
          {companies && companies.length === 0 && (
            <div
              style={{ color: "var(--muted)", fontSize: 13, padding: "10px 0" }}
            >
              No company groupings available for this entry.
            </div>
          )}
          {sortedCompanies && sortedCompanies.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginTop: 10,
              }}
            >
              {sortedCompanies.map((company) => {
                const companyId = company.companyId || "unclassified";
                const isOpen = activeCompanyId === companyId;
                const hasDiff = company.matchStatus === "mismatched";
                const hasAggregateIssue = !company.employeeAggregateMatches;

                return (
                  <div
                    key={companyId}
                    className="company-accordion-card"
                    style={{
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius)",
                      background: "var(--surface)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      className="company-accordion-header"
                      onClick={() => toggleCompany(companyId)}
                      style={{
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        userSelect: "none",
                        background: "var(--surface-2)",
                        borderBottom: isOpen
                          ? "1px solid var(--border)"
                          : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "var(--ink)" }}>
                          {company.companyName || "Unclassified"}
                        </span>
                        {company.companyId && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              background: "var(--surface-3)",
                              padding: "1px 6px",
                              borderRadius: 4,
                            }}
                          >
                            {company.companyId}
                          </span>
                        )}
                        {hasAggregateIssue && (
                          <span
                            title="The sum of transaction-level lines does not equal the company total. Check the breakdown for discrepancies."
                            style={{
                              color: "var(--danger)",
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "help",
                            }}
                          >
                            <Icon name="alert-triangle" size={15} />
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            background: hasDiff
                              ? "var(--danger-bg, #fff5f5)"
                              : "var(--success-bg)",
                            color: hasDiff
                              ? "var(--danger, #ff5252)"
                              : "var(--success)",
                            border: hasDiff
                              ? "1px solid var(--danger-border, #ffd8d8)"
                              : "1px solid var(--success-border)",
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: "bold",
                          }}
                        >
                          {hasDiff ? "Mismatched" : "Matched"}
                        </span>
                        <Icon
                          name={isOpen ? "chevron-up" : "chevron-down"}
                          size={16}
                          style={{ color: "var(--muted)" }}
                        />
                      </div>
                    </div>

                    {/* Body */}
                    {isOpen && (
                      <div
                        className="company-accordion-body"
                        style={{ padding: 16 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                            marginBottom: 14,
                            paddingBottom: 10,
                            borderBottom: "1px solid var(--border)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 20,
                              fontSize: 12,
                              color: "var(--ink-2)",
                            }}
                          >
                            <div style={{ width: 220 }}>
                              Upload Amount:{" "}
                              <strong className="mono">
                                {fmtCcy(company.uploadAmount)}
                              </strong>
                            </div>
                            <div style={{ width: 220 }}>
                              Payments Amount:{" "}
                              <strong className="mono">
                                {fmtCcy(company.paymentsAmount)}
                              </strong>
                            </div>
                            <div
                              style={{
                                color: hasDiff ? "var(--danger)" : "inherit",
                              }}
                            >
                              Amount Diff:{" "}
                              <strong className="mono">
                                {hasDiff && company.diffAmount > 0 ? "+" : ""}
                                {fmtCcy(company.diffAmount)}
                              </strong>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 20,
                              fontSize: 12,
                              color: "var(--ink-2)",
                            }}
                          >
                            <div style={{ width: 220 }}>
                              Upload Units:{" "}
                              <strong className="mono">
                                {fmtUnits(company.uploadUnits)}
                              </strong>
                            </div>
                            <div style={{ width: 220 }}>
                              Payments Units:{" "}
                              <strong className="mono">
                                {fmtUnits(company.paymentsUnits)}
                              </strong>
                            </div>
                            <div
                              style={{
                                color:
                                  Math.abs(company.diffUnits) > 0.01
                                    ? "var(--danger)"
                                    : "inherit",
                              }}
                            >
                              Units Diff:{" "}
                              <strong className="mono">
                                {company.diffUnits > 0 ? "+" : ""}
                                {fmtUnits(company.diffUnits)}
                              </strong>
                            </div>
                          </div>
                        </div>

                        <div className="narr-tbl">
                          <div
                            className="narr-head"
                            style={{
                              display: "inline-grid",
                              gridTemplateColumns:
                                "110px 140px 100px 100px 90px 100px 100px 90px 300px",
                              width: "max-content",
                              minWidth: "100%",
                            }}
                          >
                            <div>ID</div>
                            <div>Name</div>
                            <div style={{ textAlign: "right" }}>
                              Upload (GHS)
                            </div>
                            <div style={{ textAlign: "right" }}>
                              Payments (GHS)
                            </div>
                            <div style={{ textAlign: "right" }}>Diff (GHS)</div>
                            <div style={{ textAlign: "right" }}>
                              Upload (Units)
                            </div>
                            <div style={{ textAlign: "right" }}>
                              Payments (Units)
                            </div>
                            <div style={{ textAlign: "right" }}>
                              Diff (Units)
                            </div>
                            <div>Narration</div>
                          </div>
                          {company.contributions.map((emp) => {
                            const isAmtDiscrepant =
                              Math.abs(emp.diffAmount) > 0.01;
                            const isUnitsDiscrepant =
                              Math.abs(emp.diffUnits) > 0.01;
                            const isEmpDiscrepant =
                              isAmtDiscrepant || isUnitsDiscrepant;
                            return (
                              <div
                                key={emp.id}
                                className={`narr-row ${
                                  isEmpDiscrepant ? "is-deviated" : ""
                                }`}
                                style={{
                                  display: "inline-grid",
                                  gridTemplateColumns:
                                    "110px 140px 100px 100px 90px 100px 100px 90px 300px",
                                  width: "max-content",
                                  minWidth: "100%",
                                  ...(isEmpDiscrepant
                                    ? {
                                        borderLeft: "3px solid var(--danger)",
                                        background: "#fff5f5",
                                      }
                                    : {}),
                                }}
                              >
                                <div className="mono">
                                  {emp.memberId || "—"}
                                </div>
                                <div>{emp.memberName || "—"}</div>
                                <div
                                  className="mono"
                                  style={{ textAlign: "right" }}
                                >
                                  {fmtCcy(emp.uploadAmount)}
                                </div>
                                <div
                                  className="mono"
                                  style={{ textAlign: "right" }}
                                >
                                  {fmtCcy(emp.paymentsAmount)}
                                </div>
                                <div
                                  className="mono"
                                  style={{
                                    textAlign: "right",
                                    color: isAmtDiscrepant
                                      ? "var(--danger)"
                                      : "inherit",
                                    fontWeight: isAmtDiscrepant
                                      ? 600
                                      : "normal",
                                  }}
                                >
                                  {isAmtDiscrepant && emp.diffAmount > 0
                                    ? "+"
                                    : ""}
                                  {fmtCcy(emp.diffAmount)}
                                </div>
                                <div
                                  className="mono"
                                  style={{ textAlign: "right" }}
                                >
                                  {fmtUnits(emp.uploadUnits)}
                                </div>
                                <div
                                  className="mono"
                                  style={{ textAlign: "right" }}
                                >
                                  {fmtUnits(emp.paymentsUnits)}
                                </div>
                                <div
                                  className="mono"
                                  style={{
                                    textAlign: "right",
                                    color: isUnitsDiscrepant
                                      ? "var(--danger)"
                                      : "inherit",
                                    fontWeight: isUnitsDiscrepant
                                      ? 600
                                      : "normal",
                                  }}
                                >
                                  {isUnitsDiscrepant && emp.diffUnits > 0
                                    ? "+"
                                    : ""}
                                  {fmtUnits(emp.diffUnits)}
                                </div>
                                <div
                                  className="narr-text"
                                  title={emp.narration || ""}
                                >
                                  {emp.narration || "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "transactions" && (
        <>
          <div
            className="section-label"
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Transaction details · Underlying transactions</span>
            {txs && (
              <span
                style={{ color: "var(--muted)", fontSize: 12, fontWeight: 500 }}
              >
                {txs.length} of {row.txCount.toLocaleString()} shown
              </span>
            )}
          </div>
          {txs === null && !loadError && (
            <div
              style={{ color: "var(--muted)", fontSize: 13, padding: "10px 0" }}
            >
              Loading transactions…
            </div>
          )}
          {loadError && (
            <div
              style={{
                color: "var(--danger)",
                fontSize: 13,
                padding: "10px 0",
              }}
            >
              {loadError}
            </div>
          )}
          {txs && txs.length === 0 && !loadError && (
            <div
              style={{ color: "var(--muted)", fontSize: 13, padding: "10px 0" }}
            >
              No transactions are recorded against this product and value date.
            </div>
          )}
          {txs && txs.length > 0 && (
            <div className="narr-tbl">
              <div
                className="narr-head"
                style={{
                  display: "inline-grid",
                  gridTemplateColumns:
                    "130px 140px 130px 120px 100px 120px 350px",
                  width: "max-content",
                  minWidth: "100%",
                }}
              >
                <div>Value date</div>
                <div>Transaction date</div>
                <div style={{ textAlign: "right" }}>Amount</div>
                <div style={{ textAlign: "right" }}>Unit</div>
                <div>Dr/Cr</div>
                <div>Reference</div>
                <div>Transaction details</div>
              </div>
              {shown.map((tx) => {
                const signedAmount =
                  tx.transactionAmount == null
                    ? null
                    : tx.drcr === "D"
                    ? -Math.abs(tx.transactionAmount)
                    : Math.abs(tx.transactionAmount);
                const amountColor =
                  signedAmount == null
                    ? "var(--muted)"
                    : signedAmount < 0
                    ? "var(--danger)"
                    : "inherit";
                const explanation = explanationsByRow.get(tx.id);
                return (
                  <React.Fragment key={tx.id}>
                    <div
                      className={`narr-row ${
                        tx.deviated ? "is-deviated" : ""
                      } ${tx.pending ? "is-pending" : ""}`}
                      style={{
                        display: "inline-grid",
                        gridTemplateColumns:
                          "130px 140px 130px 120px 100px 120px 350px",
                        width: "max-content",
                        minWidth: "100%",
                      }}
                    >
                      <div>{formatFriendly(tx.valueDate)}</div>
                      <div>
                        {tx.transactionDate
                          ? formatFriendly(tx.transactionDate)
                          : "—"}
                      </div>
                      <div
                        className="mono"
                        style={{
                          textAlign: "right",
                          color: amountColor,
                          fontWeight: 600,
                        }}
                      >
                        {signedAmount == null
                          ? "—"
                          : (signedAmount < 0 ? "−" : "") +
                            fmtCcy(Math.abs(signedAmount))}
                      </div>
                      <div className="mono" style={{ textAlign: "right" }}>
                        {tx.units == null ? "—" : fmtUnits(tx.units)}
                      </div>
                      <div>{tx.drcr === "D" ? "Debit" : "Credit"}</div>
                      <div className="mono">
                        {tx.transactionReference || "—"}
                      </div>
                      <div
                        className="narr-text"
                        title={tx.transactionDetails || ""}
                      >
                        {tx.transactionDetails || "—"}
                      </div>
                    </div>
                    {explanation && (
                      <RowProblemCard explanation={explanation} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {txs && txs.length > 5 && (
            <button
              className="narr-toggle"
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? "Show less"
                : `Show all ${txs.length} sampled transactions`}
            </button>
          )}
        </>
      )}
    </>
  );
};

export default NarrationSection;
